import type {Delimiter, ListItem} from 'mdast'
import {EXIT, SKIP, visit} from 'unist-util-visit'

const defaultUnorderedListMarker = '* '

interface OrderedListMarker {
  prefix: string
  digits: number
  suffix: string
}

const OrderedListMarker = {
  first: {prefix: '', digits: 1, suffix: '. '},
  /** @see https://github.github.com/gfm/#ordered-list-marker */
  parse(raw: string) {
    // Theoretically there wouldn't ever be a whitespace prefix since that would be a listItemIndent token, but it
    // doesn't hurt to allow it just in case
    const match = raw.match(/^(?<prefix>\s*)(?<digits>\d{1,9})(?<suffix>[.)]\s*)$/)?.groups as
      | {
          prefix: string
          digits: string
          suffix: string
        }
      | undefined
    return match ? {...match, digits: parseInt(match.digits, 10)} : undefined
  },
  serialize(marker: OrderedListMarker) {
    return `${marker.prefix}${marker.digits}${marker.suffix}`
  },
  increment(marker: OrderedListMarker) {
    // The spec states that markers cannot have more than nine digits
    return {...marker, digits: Math.min(marker.digits + 1, 999_999_999)}
  },
  reset(marker: OrderedListMarker) {
    return {...marker, digits: 1}
  },
}

interface TasklistPrefix {
  prefix: string
  value: string
  suffix: string
}

const TasklistPrefix = {
  checked: {prefix: '[', value: 'x', suffix: '] '},
  unchecked: {prefix: '[', value: ' ', suffix: '] '},
  /** @see https://github.github.com/gfm/#task-list-item-marker */
  parse(raw: string) {
    return raw.match(/^(?<prefix>\[)(?<value>[ xX])(?<suffix>\]\s*)$/)?.groups as TasklistPrefix | undefined
  },
  serialize(marker: TasklistPrefix) {
    return `${marker.prefix}${marker.value}${marker.suffix}`
  },
}

/** Get the next list item marker string from this item. For unordered items, returns the same marker. */
export function buildNextMarkerSyntax(marker: string) {
  const ordered = OrderedListMarker.parse(marker)
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.increment(ordered)) : marker
}

/** Get the marker string for the first item matching this list. */
export function buildResetMarkerSyntax(marker: string) {
  const ordered = OrderedListMarker.parse(marker)
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.reset(ordered)) : marker
}

/** Get the marker string for the first item in a new list. */
export function buildFirstMarkerSyntax(ordered: boolean) {
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.first) : defaultUnorderedListMarker
}

/** Create tasklist prefix syntax. */
export function buildTasklistPrefixSyntax(checked: boolean) {
  return TasklistPrefix.serialize(checked ? TasklistPrefix.checked : TasklistPrefix.unchecked)
}

export function isTasklistPrefix(prefix: string) {
  return TasklistPrefix.parse(prefix) !== undefined
}

/** Get the string (indentation whitespace) to continue this list item on a following line. */
export function buildItemContinuationSyntax(marker: string): string {
  return ' '.repeat(marker.length)
}

/** Search for the list item marker and tasklist prefix in this item. */
export function findItemPrefixes(item: ListItem): {listItemPrefix?: Delimiter; taskListPrefix?: Delimiter} {
  const result: {listItemPrefix?: Delimiter; taskListPrefix?: Delimiter} = {}

  // We can't just look in `item.children` because the prefix will likely be inside a paragraph or potentially deeper
  visit(item, node => {
    if (result.listItemPrefix) {
      if (node.type === 'delimiter' && node.tokenType === 'taskListCheck') result.taskListPrefix = node

      return EXIT
    }

    if (node.type === 'delimiter' && node.tokenType === 'listItemPrefix') result.listItemPrefix = node

    // This shouldn't happen because we should always find a prefix before we ever find a nested list, but just
    // in case the tree is invalid we wouldn't want to accidentally return a prefix from a sub-item.
    if (node.type === 'list') return SKIP
  })

  return result
}
